// contexts/ThemeContext.js — light/dark theme management
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'photoqa-theme';
const ThemeContext = createContext(null);

function detectInitial() {
  if (typeof document !== 'undefined') {
    const fromDom = document.documentElement.getAttribute('data-theme');
    if (fromDom === 'light' || fromDom === 'dark') return fromDom;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(detectInitial);
  const [followSystem, setFollowSystem] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === null; } catch { return true; }
  });

  // Apply theme to <html> + update mobile status bar color
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (meta) meta.setAttribute('content', theme === 'light' ? '#ffffff' : '#0f1117');
  }, [theme]);

  // Listen for system preference change when in 'follow system' mode
  useEffect(() => {
    if (!followSystem || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const handler = e => setThemeState(e.matches ? 'light' : 'dark');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [followSystem]);

  const setTheme = useCallback(next => {
    setThemeState(next);
    setFollowSystem(false);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  const resetToSystem = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setFollowSystem(true);
    const system = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    setThemeState(system);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, followSystem, resetToSystem }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
